<template>
  <section>
    <!-- Modal Trapping Section -->
    <div aria-hidden="true"
         @focus="selectFirstElement()"
         tabindex="0" />
    <span v-if="isLoading" class="loader_container">
      <div class="loader" /> Loading...
    </span>
    <!-- Modal Trapping Ends -->
    <form class="form" :class="{ 'oas-form' : $isOhioScience || $isOasam }">
      <div v-if="!$isOasam" class="form-type-title" :class="{ 'is-size-5 has-text-weight-semibold' : isProjectSubmitForm }" v-html="formInfo.display_name"></div>
      <p class="has-text-danger mt-2 required-fields" :class="{ 'is-invisible' : hideRequiredLabelText }">(&#42; = {{$t('yscCheck.required')}})</p>
      <div class="questions is-block mb-2" v-for="(question, index) in formQuestionsList" :key="question.id">
        <fieldset v-if="!question.isHidden" class="is-flex question-container">
          <div class="label-container">
            <label class="label" :class="{ 'has-text-centered is-size-6 px-4' : question.type === QuestionTypeAcknowledgement }"
                   :aria-label="getQuestionsLabel(question)"
                   v-html="getQuestionsLabel(question)">
            </label>
            <span v-if="question.isMandatory" class="has-text-danger">&#42;</span>
            <tool-tip v-if="showToolTipIcon(question.label)" :toolTipText="formToolTipText(question.label)" class="pl-2 mb-1"/>
          </div>
          <dropdown-basic v-if="question.type === QuestionTypeList"
                            :inputName="question.label"
                            :items="question.options"
                            :value="question.dropdownAnswer"
                            @input="handleDropDownAnswer($event, question)"
                            :placeholder="'Please select value'"
                            width="100%"
                            :isUp="$isOasam ? false : (index >= ((formQuestionsList.length - 1) / 2))">
          </dropdown-basic>
          <div class="pb-4 mx-auto" v-if="question.type === QuestionTypeAcknowledgement">
            <div class="radio-container">
              <label class="radio-label" v-for="option in question.options"
                     :key="option.id" >
                <input type="radio"
                       class="is-hidden"
                       :name="question.label"
                       :value="question.dropdownAnswer"
                       @change="handleAcknowledgementAnswers(option)"/>
                <div class="button is-primary has-text-weight-semibold">{{ option.label }}</div>
              </label>
            </div>
          </div>
          <input-basic v-if="(!question.type || [QuestionTypeText, QuestionTypeEmail, QuestionTypeNumber].includes(question.type)) && excludedTextQuestions(question)"
                         :value="question.userStringAnswer"
                         @input="handleStringAnswer($event, question)"
                         :ref="question"
                         placeholder="Please type answer"
                         width="100%"
                         style="width: 100%">
          </input-basic>
          <div v-if="question.type === QuestionTypeMultipleChoice">
            <v-select
                placeholder="Please select all that apply"
                multiple
                :options="question.multiSelectValues"
                class="dropdown-text"
                @input="checkConditionalQuestions($event, question)"
                v-model="multiSelectAnswers[question.id]">
            </v-select>
          </div>
          <div class="checkbox-container is-flex" v-if="question.type === QuestionTypeRadio">
            <div class="radio-container">
              <label class="radio-label" v-for="option in question.options"
                     :key="option.id" >
                <input type="radio"
                       :name="question.label"
                       :value="option.label"
                       v-model="question.userStringAnswer">
                &nbsp;{{ option.label }}
              </label>
            </div>
          </div>
          <div class="is-flex" v-if="question.type === QuestionTypeCheckbox">
            <div class="checkbox-container"
                 :class="{'checkbox-center' : question.label.toLowerCase() === 'language'}">
              <span v-for="option in question.options" :key="option.id">
                <label
                       class="checkbox-label customCheck">
                  <input class="checkbox-button"
                         type="checkbox"
                         :id="option.label"
                         :name="question.label"
                         :checked="question.userStringAnswer.includes(option.label)"
                         :value="option.label"
                         @change="handleFormCheckbox(question, index, option)">
                  &nbsp;{{ option.label }}
              </label>
              </span>
            </div>
          </div>
          <div v-if="(question.label === 'School Name')" class="school-drop">
            <input class="input"
                   type="text"
                   :placeholder="$t('yscRequiredQuestions.placeHolderSchool')"
                   v-model.trim="selectedSchool"
                   @keyup="searchForSchools(selectedSchool)"/>
            <drop-down v-if="searchSchools.length > 0 && !schoolSelected" v-click-outside="clearSchools"
                       class="search-oas-users"
                       :isHeader="true"
                       :title="''"
                       :schools="uniqueSchoolsList"
                       :isSchoolSearch="true"
                       @selectSchool="selectSchool" />
          </div>
          <div v-if="question.label === 'County'" class="school-drop">
            <input class="input"
                   type="text"
                   :placeholder="$t('ohioRequiredQuestions.placeholderCounty')"
                   v-model.trim="selectedCounty"
                   @keyup="searchForCounties(selectedCounty)"/>
            <drop-down v-if="searchCounties.length > 0 && !countySelected" v-click-outside="clearSchools"
                       class="search-oas-users"
                       :isHeader="true"
                       :title="''"
                       :counties="uniqueCountyList"
                       :isCountySearch="true"
                       @selectCounty="selectCounty" />
          </div>
          <div v-if="(question.label === 'Home County')" class="school-drop">
            <input class="input"
                   type="text"
                   :placeholder="$t('ohioRequiredQuestions.placeholderCounty')"
                   v-model.trim="selectedCounty"
                   @keyup="searchForCounties(selectedCounty)"/>
            <drop-down v-if="searchCounties.length > 0 && !countySelected" v-click-outside="clearSchools"
                       class="search-oas-users"
                       :isHeader="true"
                       :title="''"
                       :counties="uniqueCountyList"
                       :isCountySearch="true"
                       @selectCounty="selectCounty" />
          </div>
        </fieldset>
      </div>
    </form>
    <footer class="form is-flex" :class="{ 'oas-form' : $isOhioScience || $isOasam }">
      <div class="error-message-container">
        <div v-if="errorMessage" class="help has-text-danger">{{ errorMessage }}</div>
      </div>
      <p v-if="!isLoading && activeQuestionnaire.length > 1">Step {{ questionnaireForm.sort_order }} of {{ activeQuestionnaire.length }}</p>
      <div v-if="!isLoading && showSubmitButton" class="save-button-container adjust-container">
        <button class="button is-uppercase"
                v-if="apiError"
                @click="skip">
          {{ $t("signupSurvey.skip") }}
        </button>
        <button :disabled="submittingAnswers"
                class="button is-primary"
                type="submit"
                @click="submitAnswers">
          {{ submitButtonText }}
        </button>
      </div>
    </footer>
    <!-- Modal Trapping Section -->
    <div aria-hidden="true"
         @focus="selectFirstElement()"
         tabindex="0" />
    <!-- Modal Trapping Ends -->
  </section>
</template>

<script>
import Vue from 'vue'
import vSelect from 'vue-select'
import mixin from './mixin'
import 'vue-select/dist/vue-select.css'
import link from '@/mixins/link'
import i18n from '@/i18n'
import InputBasic from '@/components/Controls/GenericComponents/inputBasic'
import DropdownBasic from '@/components/Controls/GenericComponents/dropdownBasic'
import userAgent from '@/libs/user-agent'
import DropDown from '@/components/Controls/MetaCountsDropdown'
import ClickOutside from 'vue-click-outside'
import userTypes from '@/mixins/user-types'
import debounce from 'lodash/debounce'
import questionTypes from '@/mixins/question-types'
import ToolTip from '@/components/Controls/ToolTip'
import username from '@/libs/username'
import projectTemplates from '@/mixins/project-templates'

Vue.component('v-select', vSelect)

const states = {
  inProgress: 1,
  done: 2,
  error: 3
}

Vue.directive('focus', {
  inserted: function (el) {
    el.focus()
  }
})

export default {
  components: {
    InputBasic,
    DropdownBasic,
    DropDown,
    i18n,
    ToolTip
  },
  mixins: [mixin, link, userTypes, questionTypes, projectTemplates],
  props: {
    signUpForm: {
      type: Boolean,
      default: false
    },
    cancel: {
      type: Function
    },
    close: {
      type: Function
    },
    offset: {
      type: Number
    },
    limit: {
      type: Number
    },
    hasMore: {
      type: Boolean
    },
    formId: {
      type: Number
    },
    questionnaireId: {
      type: Number
    },
    projectID: {
      type: Number
    }
  },
  data: function () {
    return {
      errorMessage: '',
      searchQuery: '',
      nextGrouping: 1,
      answeredFormCheckbox: {},
      answeredCheckbox: {},
      apiError: false,
      requiredQuestions: [],
      isLoading: false,
      multiSelectAnswers: [],
      selectedSchool: '',
      searchSchools: [],
      schoolSelected: false,
      selectedCounty: '',
      searchCounties: [],
      countySelected: false,
      submittingAnswers: false,
      isUnder13AnswerYes: false
    }
  },
  mounted: function () {
    this.filterQuestions()
  },
  computed: {
    getProjectSubmission: function () {
      return this.$store.getters.getProjectSubmission
    },
    getProjectPaymentInfo: function () {
      return this.$store.getters.getProjectPaymentInfo
    },
    activeFormQuestionIndex: function () {
      return this.$store.getters.activeFormQuestionIndex
    },
    questionnaireFormCount: function () {
      return this.$store.getters.questionnaireFormCount
    },
    uniqueSchoolsList: function () {
      const filteredSchools = this.searchSchools.filter((value, index, self) =>
        index === self.findIndex((school) => (
          school.title === value.title && school.county === value.county
        ))
      )
      for (const school of filteredSchools) {
        if (school.county) {
          school.title = school.title + ', ' + school.county
        }
      }
      return filteredSchools
    },
    uniqueCountyList: function () {
      return this.searchCounties.filter((value, index, self) =>
        index === self.findIndex((school) => (
          school.county === value.county
        ))
      )
    },
    currentUser: function () {
      return this.$store.getters.currentUser
    },
    isTeacher: function () {
      return this.currentUser.type?.includes(this.UserTypeTeacher)
    },
    isStudent: function () {
      return this.currentUser.type?.includes(this.UserTypeStudent)
    },
    isJudge: function () {
      return this.currentUser.type?.includes(this.UserTypeJudge)
    },
    isMentor: function () {
      return this.currentUser.type?.includes(this.UserTypeMentor)
    },
    isTeacherRegistration: function () {
      return this.formInfo?.name.includes('Teacher_Registration') && (this.$route.query.userType?.includes(this.UserTypeTeacher) || this.isTeacher)
    },
    isStudentRegistration: function () {
      return this.formInfo?.name.includes('Student_Registration') && (this.$route.query.userType?.includes(this.UserTypeStudent) || this.isStudent)
    },
    isJudgeRegistration: function () {
      return this.formInfo?.name.includes('Judge_Registration') && (this.$route.query.userType?.includes(this.UserTypeJudge) || this.isJudge)
    },
    isMentorRegistration: function () {
      return this.formInfo?.name.includes('Mentor_Registration') && (this.$route.query.userType?.includes(this.UserTypeMentor) || this.isMentor)
    },
    isPaymentModal: function () {
      return this.formInfo?.name.includes('Project_Submission_Payment_Info')
    },
    formInfo: function () {
      return this.$store.getters.formInfo
    },
    showSubmitButton: function () {
      return !this.isPaymentModal
    },
    hideRequiredLabelText: function () {
      return this.isPaymentModal
    },
    submitButtonText: function () {
      if (!this.hasMore && this.$isOhioScience) {
        return i18n.t('signupSurvey.submitAndFinishButton')
      } else if (this.hasMore) {
        return i18n.t('signupSurvey.submitAndContinueButton')
      } else {
        return i18n.t('signupSurvey.continueButton')
      }
    },
    questionnaireForm: function () {
      return this.$store.getters.questionnaireForm(this.activeFormQuestionIndex)
    },
    activeQuestionnaire: function () {
      return this.$store.getters.activeQuestionnaire(this.questionnaireForm?.questionnaire_id)
    },
    formQuestionsList: function () {
      return this.$store.getters.questions(this.formId)
    },
    project: function () {
      return this.projectID ? this.$store.getters.project(this.projectID) : null
    },
    fairId: function () {
      return this.project ? this.$store.getters.templateFairId(this.project.sourceProjectID) : 0
    }
  },
  methods: {
    searchForSchools: debounce(function (searchName) {
      this.schoolSelected = false
      this.searchSchools = []

      // Strip trailing and starting whitespaces, reduce multiple whitespaces to a single space, and strip commas
      const cleanedQuery = searchName.trim().replace(/\s+/g, ' ').replace(/,/g, '')

      // Check if the first word has a minimum length of 2 characters
      if (cleanedQuery.length >= 2) {
        // Search for schools whose names start with the first word
        const query = `\\b${cleanedQuery.toLowerCase()}`

        this.$store.dispatch('getAllLikeSchoolName', {
          limit: 50,
          query: query,
          searchBy: 'title'
        }).then((response) => {
          this.searchSchools = response.schools
        })
      }
    }, 200),
    selectSchool: function (school) {
      this.selectedSchool = school.title
      this.schoolSelected = true
    },
    selectCounty: function (county) {
      this.selectedCounty = county.county
      this.countySelected = true
    },
    showToolTipIcon: function (questionLabel) {
      return ['County', 'Home County', 'School Name', "Parent's phone number", 'Phone Number', 'Phone', 'Contact phone'].includes(questionLabel)
    },
    formToolTipText: function (questionLabel) {
      switch (questionLabel) {
        case 'County':
        case 'Home County':
          return this.$t('signupSurvey.countyToolTip')
        case 'School Name':
          return this.$t('signupSurvey.schoolToolTip')
        case "Parent's phone number":
          return this.$t('signupSurvey.phoneNumberToolTip')
        case 'Phone Number':
          return this.$t('signupSurvey.phoneNumberToolTip')
        case 'Phone':
          return this.$t('signupSurvey.phoneNumberToolTip')
        case 'Contact phone':
          return this.$t('signupSurvey.phoneNumberToolTip')
      }
    },
    clearSchools: function () {
      this.searchSchools = []
      this.selectedSchool = ''
    },
    searchForCounties: debounce(function (searchName) {
      this.countySelected = false
      this.searchCounties = []

      // Strip trailing and starting whitespaces, reduce multiple whitespaces to a single space, and strip commas
      const cleanedQuery = searchName.trim().replace(/\s+/g, ' ').replace(/,/g, '')

      // Check if the first word has a minimum length of 2 characters
      if (cleanedQuery.length >= 2) {
        // Search for counties whose names start with the first word
        const query = `\\b${cleanedQuery.toLowerCase()}`

        this.$store.dispatch('getAllLikeSchoolName', {
          limit: 50,
          query: query,
          searchBy: 'county'
        }).then((response) => {
          this.searchCounties = response.schools
        })
      }
    }, 200),
    excludedTextQuestions: function (question) {
      return !['School Name', 'County', 'Home County', ''].includes(question.label)
    },
    getQuestionsLabel: function (question) {
      return question.label ? question.label : question.question
    },
    selectFirstElement: function () {
      this.$refs.first_element.focus()
    },
    isMobile: function () {
      return userAgent.isIOS() || userAgent.isAndroid()
    },
    addNamesToState: function () {
      const data = [this.firstName, this.lastName]
      this.$store.dispatch('setSignupNames', data)
    },
    handleAcknowledgementAnswers: function (answer) {
      const acknowledgementQuestions = this.formQuestionsList.filter(e => e.saveAnswer === true)
      this.handleDropDownAnswer(answer, acknowledgementQuestions[0])
      this.$nextTick(() => {
        this.submitAnswers()
      })
    },
    handleFormCheckbox: function (question, index, value) {
      // reset
      this.handleStringAnswer('', question)
      // calculate new value
      if (this.answeredFormCheckbox[index] === undefined) {
        this.answeredFormCheckbox[index] = [value]
      } else {
        if (this.answeredFormCheckbox[index].includes(value)) {
          this.answeredFormCheckbox[index] = this.answeredFormCheckbox[index].filter(e => e !== value)
          if (this.answeredFormCheckbox[index].length === 0) {
            this.answeredFormCheckbox[index] = undefined
          }
        } else {
          this.answeredFormCheckbox[index].push(value)
        }
      }
      // save answer
      this.handleStringAnswer(this.answeredFormCheckbox[index].join(','), question)
    },
    handleDropDownAnswer: function (answer, question) {
      const checkConditionalQuestion = true
      this.handleAnswer('setDropDownAnswer', answer, question, checkConditionalQuestion)
    },
    handleStringAnswer: function (answer, question) {
      this.handleAnswer('setStringAnswer', answer, question)
    },
    checkConditionalQuestions: function (answers, question) {
      this.multiSelectAnswers[question.id] = answers
      this.handleConditionalQuestions('handleMultiselectConditional', question, answers)
    },
    handleConditionalQuestions: function (dispatch, question, answers) {
      this.$store.dispatch(dispatch, { formId: this.formId, parentQuestion: question, answers: answers })
    },
    handleAnswer: function (dispatch, answer, question, checkConditionalQuestion) {
      this.$store.dispatch(dispatch, {
        formId: this.formId, questionId: question.id, answer: answer, checkConditionalQuestion: checkConditionalQuestion
      }).then(() => {
        this.formQuestionsList = this.$store.getters.questions(this.formId)
      })
    },
    skip: function () {
      if (this.signUpForm) {
        this.state = states.done
        this.$emit('close')
      } else {
        this.$emit('modalSuccess')
        this.close()
      }
    },
    submitAnswers: function () {
      this.submittingAnswers = true
      const userAnswers = []
      this.errorMessage = ''
      if (!this.getProjectPaymentInfo) {
        this.formQuestionsList.forEach((question, index) => {
          if (!question.isHidden) {
            const dropdownAnswers = []

            if (['County', 'Home County'].includes(question.label)) {
              if (this.countySelected && this.selectedCounty !== '') {
                this.handleStringAnswer(this.selectedCounty, question)
              } else if (!this.countySelected && this.selectedCounty !== '') {
                this.errorMessage = i18n.t('error.invalid.ohioCounty')
              }
            }
            if (question.label === 'School Name') {
              if (this.schoolSelected && this.selectedSchool !== '') {
                this.handleStringAnswer(this.selectedSchool, question)
              } else if (!this.schoolSelected && this.selectedSchool !== '') {
                this.errorMessage = i18n.t('error.invalid.ohioSchool')
              }
            }

            if (question.isMandatory && !this.errorMessage) {
              if ((!question.dropdownAnswer.label && question.type === this.QuestionTypeList) ||
                  (!question.userStringAnswer && [this.QuestionTypeText, this.QuestionTypeEmail, this.QuestionTypeNumber].includes(question.type)) ||
                  (question.type === this.QuestionTypeMultipleChoice && this.multiSelectAnswers[question.id] && this.multiSelectAnswers[question.id].length === 0) ||
                  (question.type === this.QuestionTypeCheckbox && !this.answeredFormCheckbox[index])) {
                this.errorMessage = `"${question.label}" is required.`
              } else if (question.type === this.QuestionTypeEmail) {
                if (!username.isValidEmail(question.userStringAnswer)) {
                  this.errorMessage = `"Please enter a valid ${question.label}".`
                }
              } else if (question.type === this.QuestionTypeNumber && ['Phone Number', 'Parent\'s phone number', 'Contact phone'].includes(question.label)) {
                if (!/^(\d{10}|\d{3}-\d{3}-\d{4})$/.test(question.userStringAnswer)) {
                  this.errorMessage = 'Phone number should be 10 digits long or in the format xxx-xxx-xxxx.'
                }
              } else if (['Zip', 'Home Address - ZIP'].includes(question.label)) {
                if (question.userStringAnswer.length !== 5) {
                  this.errorMessage = 'Zip codes should be 5 digits long.'
                }
              }
            }

            if (question.type === this.QuestionTypeList && question.dropdownAnswer) {
              dropdownAnswers.push(question.dropdownAnswer)
            }

            if (question.type === this.QuestionTypeCheckbox && this.answeredFormCheckbox[index] && this.answeredFormCheckbox[index].length > 0) {
              this.handleStringAnswer('', question)
              this.answeredFormCheckbox[index].forEach((checkBoxAnswer) => {
                dropdownAnswers.push(checkBoxAnswer)
              })
            }

            if (question.type === this.QuestionTypeMultipleChoice && this.multiSelectAnswers[question.id] && this.multiSelectAnswers[question.id].length > 0) {
              this.multiSelectAnswers[question.id].forEach((answer) => {
                question.options.forEach((option) => {
                  if (option.label === answer) {
                    dropdownAnswers.push(option)
                    return true
                  }
                })
              })
            }

            if (this.errorMessage) {
              this.submittingAnswers = false
            }
            const userAnswer = {
              questionId: question.id,
              answer: question.userStringAnswer ? question.userStringAnswer : null,
              dropdownAnswer: dropdownAnswers.length > 0 ? dropdownAnswers : null,
              questionType: question.type
            }
            userAnswers.push(userAnswer)
          }
        })
      }
      if (this.getProjectPaymentInfo) {
        const acknowledgementQuestions = this.formQuestionsList.filter(e => e.saveAnswer === true)
        if (acknowledgementQuestions[0].dropdownAnswer.id !== 0) {
          const question = acknowledgementQuestions[0]
          const paymentAnswer = []
          paymentAnswer.push(question.dropdownAnswer)
          const userAnswer = {
            questionId: question.id,
            dropdownAnswer: paymentAnswer.length > 0 ? paymentAnswer : null,
            questionType: question.type
          }
          userAnswers.push(userAnswer)
        }
      }
      var isAnswerUpdate = false
      if (this.getProjectSubmission || this.getProjectPaymentInfo) {
        isAnswerUpdate = true
      }
      const data = {
        answers: userAnswers,
        formId: this.formInfo.id,
        formName: this.formInfo.name,
        questionnaireId: this.questionnaireId,
        isAnswerUpdate: isAnswerUpdate,
        projectID: this.projectID,
        sourceProjectId: this.project?.sourceProjectID,
        isProjectSubmission: this.getProjectSubmission,
        isProjectPaymentSubmission: this.getProjectPaymentInfo,
        fairId: this.fairId
      }
      if (!this.errorMessage) {
        this.$store.dispatch('saveUserFormQuestionAnswers', data).then(() => {
          this.submittingAnswers = false
          this.$emit('doneAnswerSubmission')
          // Get more questions
          this.isLoading = true
          this.submittingAnswers = false
          if (this.activeFormQuestionIndex < this.questionnaireFormCount - 1) {
            this.$store.dispatch('setCurrentQuestionnaireFormIndex', this.activeFormQuestionIndex + 1)
          } else {
            if (this.$isOhioScience) {
              if (this.isTeacherRegistration) {
                this.myClassesRedirect()
              } else if (this.isJudgeRegistration) {
                this.judgeConfirmationRedirect()
              } else if (this.isMentorRegistration) {
                this.mentorConfirmationRedirect()
              } else if (this.isStudentRegistration) {
                this.myProjectsRedirect()
              }
            } else if (this.$isOasam) {
              this.oasamRedirect()
            }
            this.addNamesToState()
            this.state = states.done
            this.$emit('close')
            this.$emit('modalSuccess')
          }
          data.answers = []
          data.formId = 0
          data.questionnaireId = 0
        }, () => {
          this.errorMessage = this.$t('ohioRequiredQuestions.saveError')
          this.apiError = true
          this.submittingAnswers = false
        }).finally(() => {
          this.isLoading = false
          this.submittingAnswers = false
        })
      }
    },
    filterQuestions: function () {
      this.$store.dispatch('doneCustomQuestionLoading')
    },
    myClassesRedirect: function () {
      this.$router.push('/profile?activeTab=TabMyClasses')
    },
    judgeConfirmationRedirect: function () {
      this.$router.push('/judgeconfirmation')
    },
    mentorConfirmationRedirect: function () {
      this.$router.push('/mentorconfirmation')
    },
    myProjectsRedirect: function () {
      this.$router.push('/viewAll/?profile=mine')
    },
    oasamRedirect: function () {
      this.$router.push(`start/template?projectID=${this.OasamTemplate}&startButton=true&draft=true&hidden=true`)
    }
  },
  directives: {
    ClickOutside
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.dropdown-text {
  font-family: 'Roboto';
  font-size: 12px;
  margin-bottom: 12px;
}

::v-deep .vs__actions::after {
  content: "\f107";
  font-family: "Font Awesome 6 Pro";
  font-weight: 300;
  font-size: 1.3rem;
  color: #005BAB;
}

::v-deep .vs__actions .vs__open-indicator {
  display: none !important;
}

::v-deep div.vs__dropdown-toggle {
  min-height: 47px;
  border: 1.5px solid #DCDCDC;
}

.v-select {
  ::placeholder {
    color: #4a4a4a;
    font-family: 'Roboto';
    font-size: 0.85rem;
  }
}

// Radio & Checkbox colours
input[type='radio'] {
  -webkit-appearance:none;
  width:20px;
  height:20px;
  border:1px solid darkgray;
  border-radius:50%;
}
input[type='radio']:hover {
  box-shadow:0 0 5px 0px $primary inset;
}
input[type='radio']:focus {
  outline: none !important;
  border:1px solid $primary;
}

input[type='checkbox']:focus {
  outline: none !important;
  border:1px solid $primary;
}

input[type='radio']:before {
  content:'';
  display:block;
  width:60%;
  height:60%;
  margin: 20% auto;
  border-radius:50%;
}
input[type='radio']:checked:before {
  background: $primary;
}

// FORM and FOOTER layout
.form {
  font-family: 'Robotto';
}

.oas-form {
  font-family: 'cera-pro';
  width: 90%;
  max-width: 500px;
  margin: 0 auto;
  background: #FFF;
  @include mobile {
    max-width: 350px !important;
  }
}

.form-type-title {
  margin-top: 1rem;
  font-size: 1rem;
}

.is-flex {
  flex-direction: column;
}

.school-drop {
  padding-bottom: 10px;
}
@media (max-width: 360px){
  .mobile-continue-button {
    margin-top: 5px !important;
  }
}

.customCheck {
  padding: 0 5px;
}

// FORM
.required-fields {
  font-size: 0.75rem;
  text-align: right;
}
.question-container {
  .label-container {
    display: flex;
    align-items: flex-start;
  }
  .label {
    text-align: left;
    color: #262626;
    font-size: 0.85rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  .input {
    font-family: "Roboto";
    color: #343B4C;
    padding: 0.75rem;
    border-radius: 8px;
    border: 1px solid #DDD;
    background: transparent;
    width: 100%;
    font-size: 0.8rem;
    box-shadow: none;
    height: 40px;
    &:focus {
      border:1px solid lighten($primary, 10%);
      box-shadow: 0 0 0 0.125em lighten($primary, 40%);
    }
  }
  .select {
    font-size: 0.9rem;
    display: block;
    &::after {
      border-color: $grey-dark;
      top: 21px;
    }
    &:hover {
      &::after {
        border-color: $primary;
      }
    }
  }
  select:-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 #000;
  }
  .disable {
    pointer-events: none;
    opacity: 0.5;
  }
  .disable-text-input { // Work around for unwanted default Bulma styles in '.disable'
    pointer-events: none;
    opacity: 0.5;
  }
  .disable-text {
    opacity: 0.5;
  }
}
.custom-gender-container {
  padding-top: 1.8rem;
}

::v-deep .floating-label input {
  transition: all .1s ease;
  display: block;
  color: #343B4C;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #DDD;
  background: transparent;
  width: 100%;
  font-size: 0.8rem;
  box-shadow: none;
  height: 40px;
  &:focus {
    border:1px solid lighten($primary, 10%);
    box-shadow: 0 0 0 0.125em lighten($primary, 40%);
  }
}

// CUSTOM CHECKBOX STYLES
.terms-of-use-checkboxes {
  padding-top: 0.5rem;
  text-align: left;
  position: relative;
  .disable-checkbox {
    opacity: 0.5;
    pointer-events: none;
  }
}
// hiding the browser's default checkbox
.default-checkbox {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}
.default-questions{
  margin-left: 5px;
}

.default-help {
  text-align: left;
  font-size: .8rem;
}

.search-form-options {
  position: relative;
  right: 0px;
  top: 0px;
  width: 350px;
  ::v-deep .dropdown-wrapper, .show-dropdown {
    max-width: 350px;
    width: 350px;
  }
}
.search-oas-users {
  position: relative;
  left: -10px;
  top: 2px;
  width: 475px;
  ::v-deep .dropdown-wrapper, .show-dropdown {
    max-width: 500px;
    width: 475px;
  }
}

.radio {
  &-container {
    display: flex;
    flex-direction: row;
    @include mobile() {
      flex-wrap: wrap;
    }
  }
  &-label {
    padding: 5px;
  }
}
// Checkbox
.checkbox {
  &-center {
    justify-content: space-evenly;
  }
  &-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    user-select: none;
    max-width: 350px;
    &:not(:nth-of-type(3)) {
      padding-bottom: 1rem;
    }
    label {
      font-size: .8rem;
      display: inline-flex;
      position: relative;
      padding: 5px;
    }
  }
  &-button {
    appearance: none;
    border: 1px solid #d3d3d3;
    width: 15px;
    height: 15px;
    content: none;
    margin: 1px 2px 0 2px;
    box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;

    &:checked {
      padding-top: 2px;
      content: none;

      &:before {
        position: absolute;
        color: $primary !important;
        content: "\00A0\2713\00A0" !important;
        font-weight: bolder;
        font-size: 15px;
        margin: -5px 0 0 -3px;
      }
    }
  }
}

// Inputs
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}

// FOOTER
.error-message-container {
  flex-shrink: 0;
  margin: 0 0 10px 0;
  width: 100%;
}
.save-button-container {
  flex-shrink: 0;
  align-items: center;
  margin: 0.5rem 0 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #d3d3d3;
  @include mobile {
    margin: 1rem 0 20px 0;
  }
  .button {
    width: 90%;
    border-radius: 8px !important;
    flex: 0 0 auto;
    height: auto;
    padding: .5rem 1.2rem;
    text-transform: uppercase;
    font-size: 0.9rem;
    position: static;
    @include mobile {
      margin: 0 0 10px 0;
    }
  }
}

.gas-spacing {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  @include mobile {
    margin-top: 15px;
    flex-direction: column;
  }
  &-left {
    width: 100px;
    margin-left: -10px;
    font-weight: 500;
  }
}

.loader {
  border: 5px solid $secondary; /* Light grey */
  border-top: 5px solid $primary; /* Blue */
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  animation: spin 2s linear infinite;
  &_container {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    min-height: 500px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// RESPONSIVE
@include mobile {
  .oas-form {
    width: 90%;
  }
  .search-oas-users {
    width: 335px;
  }
  .search-form-options {
    right: 0%;
  }
  .generic-dropdown {
    margin: 0 auto 0.75rem;
    width: 100%;
    min-height: 45px;
  }
}

</style>
